// ConflictCard and related types extracted from ConflictResolutionPage.

import type { JSX } from "react";
import { formatGenericRef, formatSrRef } from "../lib/refs.js";

export interface ConflictEntry {
  field_name: string;
  existing_value: unknown;
  existing_written_at: string;
  existing_channel: string;
  incoming_value: unknown;
  incoming_completed_at: string;
  incoming_channel: string;
  resolved_by: string | null;
  resolved_at: string | null;
  winning_value: unknown | null;
}

export interface ConflictReport {
  _id: string;
  sr_id: string;
  patient_id: string;
  submitted_by: string;
  completed_at: string;
  conflicts: ConflictEntry[];
}

export interface ResolvePayload {
  report_id: string;
  field_name: string;
  winning_value: unknown;
}

export function filterOpenConflicts(reports: ConflictReport[] | undefined): ConflictReport[] {
  return reports?.filter((r) => r.conflicts.some((c) => c.winning_value === null)) ?? [];
}

export function formatVal(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
}

export function ConflictCard({ report, onResolve }: { report: ConflictReport; onResolve: (payload: ResolvePayload) => void }): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Report {formatGenericRef(report._id, "RPT")}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            SR: {formatSrRef({ _id: report.sr_id })} · Submitted {new Date(report.completed_at).toLocaleString()}
          </p>
        </div>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
          {report.conflicts.length} conflict{report.conflicts.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {report.conflicts.map((c) => (
          <div key={c.field_name} className="px-5 py-4 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">{c.field_name.replace(/_/g, " ")}</p>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-500 mb-1">Existing ({c.existing_channel})</p>
                <p className="text-sm text-slate-900 font-mono">{formatVal(c.existing_value)}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(c.existing_written_at).toLocaleString()}</p>
                {c.winning_value === null && (
                  <button
                    onClick={() => onResolve({ report_id: report._id, field_name: c.field_name, winning_value: c.existing_value })}
                    className="mt-2 text-xs text-navy-700 hover:text-navy-900 font-medium"
                  >
                    Keep this value
                  </button>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-600 mb-1">Incoming ({c.incoming_channel})</p>
                <p className="text-sm text-slate-900 font-mono">{formatVal(c.incoming_value)}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(c.incoming_completed_at).toLocaleString()}</p>
                {c.winning_value === null && (
                  <button
                    onClick={() => onResolve({ report_id: report._id, field_name: c.field_name, winning_value: c.incoming_value })}
                    className="mt-2 text-xs text-navy-700 hover:text-navy-900 font-medium"
                  >
                    Use this value
                  </button>
                )}
              </div>
            </div>

            {c.winning_value !== null && (
              <p className="text-xs text-green-600 font-medium">
                Resolved: {formatVal(c.winning_value)} (by {formatGenericRef(c.resolved_by, "U")} at {c.resolved_at ? new Date(c.resolved_at).toLocaleString() : "—"})
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
